import { type FC } from "react";
import { Sparkles } from "lucide-react";

import parkBench from "@/shared/assets/illustrations/park-bench.svg";
import tellUsImg from "@/shared/assets/illustrations/tell-us.svg";
import giftPresentImg from "@/shared/assets/illustrations/gift-present.svg";

import { NavPill } from "@/pages/layouts/components/NavPill";
import { getRefPillText } from "@/shared/constants/constants";
import { WHY_WE_ASK, WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";
import type { FlowSection, FlowAnswers } from "@/shared/constants/flowTypes";

import { CreateAccount } from "@/pages/onboarding/components/screens/CreateAccount";
import { VerifyEmail } from "@/pages/onboarding/components/screens/VerifyEmail";
import { ScrapeLoading } from "@/pages/onboarding/components/screens/ScrapeLoading";
import { ObituaryChoice } from "@/pages/onboarding/components/screens/ObituaryChoice";
import { DecedentContactDetails } from "@/pages/onboarding/components/screens/DecedentContactDetails";
import { AccessCodeRedemption } from "@/pages/onboarding/components/screens/AccessCodeRedemption";
import { ConfirmDetails } from "@/pages/onboarding/components/screens/ConfirmDetails";
import { AttorneyRedirect } from "@/pages/onboarding/components/screens/AttorneyRedirect";
import { GreatStart } from "@/pages/onboarding/components/screens/GreatStart";
import { Relationship } from "@/shared/constants/relationshipOptions";

import { UploadId } from "@/pages/onboarding/components/screens/UploadId";
import { IdVerifying } from "@/pages/onboarding/components/screens/IdVerifying";

const RefPillHeader: FC<{ answers: FlowAnswers }> = ({ answers }) => {
  const ref = (answers["onboarding-utm"] as string) || "";
  return (
    <NavPill
      icon={Sparkles}
      label={getRefPillText(ref)}
      variant="static"
      className="border-primary text-primary"
    />
  );
};

// Pre-account flow: before user creates account
export const preAccountSection: FlowSection = {
  id: "part1-pre-account",
  title: "Getting started",
  allowSaveAndClose: false,
  saveOnEachStep: false,
  screens: [
    // Screen 1: Create Account
    {
      id: "create-account",
      layout: "full",
      hideHeader: true,
      customComponent: CreateAccount,
      hideButton: true,
    },

    // Screen 2: Verify Email (controlled by feature flag)
    {
      id: "verify-email",
      layout: "full",
      hideHeader: true,
      customComponent: VerifyEmail,
      hideButton: true,
      shouldDisplay: ({ answers }) => answers["email-verification-enabled"] === "true",
    },

    // Screen 3: Tell us about yourself
    {
      id: "about-yourself",
      layout: "full",
      textAlign: "center",
      headerComponent: RefPillHeader,
      imageSrc: tellUsImg,
      imageSize: "sm",
      title: "Let's start with you",
      subtitle: "Your name and number first, then we'll set up the estate together",
      showEncryptionNote: true,
      question: {
        id: "about-yourself",
        type: "input_field",
        required: true,
        fields: [
          {
            id: "first-name",
            label: "First name",
            placeholder: "John",
            inputType: "text",
            required: true,
            inline: true,
            errorMessage: "First name is required",
          },
          {
            id: "middle-name",
            label: "Middle name",
            placeholder: "Michael",
            inputType: "text",
            inline: true,
          },
          {
            id: "last-name",
            label: "Last name",
            placeholder: "Doe",
            inputType: "text",
            required: true,
            errorMessage: "Last name is required",
          },
          {
            id: "phone-number",
            label: "Phone number",
            tooltip: { title: WHY_WE_ASK_TITLE, content: WHY_WE_ASK.userPhone },
            placeholder: "(123) 123-1234",
            inputType: "tel",
            required: true,
            validate: (value: string) => {
              if (!value) return true;
              const digits = value.replace(/\D/g, "");
              return digits.length === 10;
            },
            errorMessage: "Enter a valid 10-digit phone number",
          },
        ],
      },
    },
  ],
};

// Post-account flow: after user verifies email
export const postAccountSection: FlowSection = {
  id: "part1-post-account",
  title: "Building your roadmap",
  allowSaveAndClose: false,
  saveOnEachStep: false,
  expectedTotalSteps: 7,
  screens: [
    // Screen 1: Access code redemption (only if entry-type is access-code)
    {
      id: "access-code-redemption",
      layout: "full",
      textAlign: "center",
      imageSrc: giftPresentImg,
      imageSize: "sm",
      imagePosition: "top",
      title: "Redeem your gift",
      subtitle: "Elayne gift codes are often sent by a family member, attorney, or funeral home",
      customComponent: AccessCodeRedemption,
      hideButton: true,
      shouldDisplay: ({ answers }) => answers["entry-type"] === "access-code",
    },

    // Screen 2: Obituary or manual entry (skip if obit URL already provided via param)
    {
      id: "has-obituary",
      layout: "full",
      textAlign: "center",
      maxWidth: "md",
      headerComponent: RefPillHeader,
      imageSrc: parkBench,
      imageSize: "sm",
      imagePosition: "top",
      title: "Tell us about the person who passed",
      subtitle:
        "Paste a link to their obituary and we'll fill in the basics for you, like names and dates",
      customComponent: ObituaryChoice,
      hideButton: true,
      shouldDisplay: ({ answers }) => answers["obit-from-param"] !== "true",
    },

    // Screen 3: Scrape loading (only if has-obituary = yes)
    {
      id: "scrape-loading",
      layout: "full",
      textAlign: "center",
      verticalCenter: true,
      hideButton: true,
      skipOnBack: true,
      customComponent: ScrapeLoading,
      shouldDisplay: ({ answers }) => answers["has-obituary"] === "yes",
    },

    // Manual path: name + dates + relationship (no address)
    {
      id: "decedent-contact-details-manual",
      layout: "full",
      headerComponent: RefPillHeader,
      customComponent: DecedentContactDetails,
      hideButton: true,
      shouldDisplay: ({ answers }) => answers["has-obituary"] !== "yes",
    },

    // Screen 6-7: Obituary path - confirm first, then contact details
    {
      id: "confirm-details-obit",
      layout: "full",
      headerComponent: RefPillHeader,
      customComponent: ConfirmDetails,
      hideButton: true,
      shouldDisplay: ({ answers }) => answers["has-obituary"] === "yes",
    },
    // decedent-contact-details-obit removed — relationship now captured on confirm screen

    // Attorney redirect interstitial (only if relationship is attorney)
    {
      id: "attorney-redirect",
      layout: "full",
      verticalCenter: true,
      customComponent: AttorneyRedirect,
      hideButton: true,
      shouldDisplay: ({ answers }) => answers["decedent-relationship"] === Relationship.Attorney,
    },

    // Screen 8: Choose your plan (temporarily disabled)
    // Disabling this also killed the new-user Slack ping, which rode on
    // ChoosePlan's `free_option_bypass`. It now fires from account creation.
    // {
    //   id: "choose-plan",
    //   layout: "full",
    //   textAlign: "center",
    //   maxWidth: "4xl",
    //   customComponent: ChoosePlan,
    //   hideButton: true,
    //   shouldDisplay: ({ answers }) =>
    //     answers["is-invited-member"] !== "true" && answers["has-paid"] !== "true",
    // },

    // Screen 9: Upload ID (invited members only)
    {
      id: "upload-id",
      layout: "full",
      customComponent: UploadId,
      hideButton: true,
      shouldDisplay: ({ answers }) => answers["is-invited-member"] === "true",
    },

    // Screen 10: ID verification (invited members only)
    {
      id: "id-verifying",
      layout: "full",
      textAlign: "center",
      verticalCenter: true,
      hideButton: true,
      skipOnBack: true,
      customComponent: IdVerifying,
      shouldDisplay: ({ answers }) => answers["is-invited-member"] === "true",
    },

    // Screen 11: Great start success screen (removed)
    {
      id: "great-start",
      layout: "full",
      customComponent: GreatStart,
      hideButton: true,
      shouldDisplay: () => false,
    },
  ],
};
